import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, Bell, CheckCheck } from 'lucide-react';
import clsx from 'clsx';
import { aaiApi } from '../services/aaiApi';
import type { AaiNotification } from '../types/aai';

/*
 * The notification bell, in every shell's chrome.
 *
 * Activity is the operating record: what happened, kept for anyone allowed to
 * read it. Notifications are the other half — what happened TO YOU, and what
 * is waiting on you. They are different questions, so they are different
 * controls sitting side by side, and this one carries the count.
 *
 * The panel lists the most recent notices and nothing more. Opening one marks
 * it read and follows its link when the link is one THIS shell can render; the
 * full inbox is behind `allHref` for the shells that have a page for it.
 *
 * That last check is the point of `scope`. The two shells do not share a route
 * table: /partner/* only resolves in the partner console, everything else only
 * in the company shell. A notification carries one link for whoever receives
 * it, and rows written before a recipient's shell existed can carry the wrong
 * one, so a link the current shell cannot open is not offered rather than
 * navigating somebody into a blank route. The row's own text still says what
 * happened, which is what the notification is for.
 */

const PANEL_LIMIT = 12;

export type BellScope = 'client' | 'partner';

const canOpen = (link: string | null, scope: BellScope): boolean => {
  if (!link || !link.startsWith('/')) return false;
  if (scope === 'partner') return link.startsWith('/partner/');
  return !link.startsWith('/partner/');
};

const timeAgo = (iso: string | null): string => {
  if (!iso) return '';
  const mins = Math.max(0, (Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${Math.floor(mins)}m ago`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
  return `${Math.floor(mins / 1440)}d ago`;
};

const countLabel = (n: number): string => {
  if (n > 9) return '9+';
  return `${n}`;
};

/* A partner's bell spans every client they serve: each notice names its
   client, and opening one switches the console to that client first. */
export interface BellClients {
  nameOf: (clientId: string) => string | undefined;
  open: (clientId: string) => void;
}

export const NotificationBell: React.FC<{ scope: BellScope; allHref?: string; clients?: BellClients }> = ({ scope, allHref, clients }) => {
  const allClients = Boolean(clients);
  const sourceOf = (n: AaiNotification): string => {
    if (!clients || !n.client_id) return '';
    return clients.nameOf(n.client_id) || '';
  };
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<AaiNotification[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);

  const refreshCount = useCallback(() => {
    aaiApi.notificationsSummary(allClients)
      .then(({ data }) => setUnread(data.unread_count))
      .catch(() => undefined);
  }, [allClients]);

  useEffect(() => {
    refreshCount();
    window.addEventListener('aai:notifications-changed', refreshCount);
    return () => window.removeEventListener('aai:notifications-changed', refreshCount);
  }, [refreshCount, location.pathname]);

  useEffect(() => {
    if (!open) return undefined;
    let live = true;
    setLoading(true);
    aaiApi.listNotifications({ limit: PANEL_LIMIT, all_clients: allClients })
      .then(({ data }) => {
        if (!live) return;
        setItems(data.notifications);
        setUnread(data.unread_count);
        setError(null);
      })
      .catch(() => {
        if (live) setError('Could not load notifications.');
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => { live = false; };
  }, [open, allClients]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event: PointerEvent) => {
      if (boxRef.current?.contains(event.target as Node)) return;
      setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const markRead = async (ids?: string[]) => {
    try {
      await aaiApi.markNotificationsRead(ids, allClients);
      setItems((prev) => prev.map((n) => {
        if (ids && !ids.includes(n.id)) return n;
        return { ...n, read: true };
      }));
      setUnread((prev) => {
        if (!ids) return 0;
        return Math.max(0, prev - ids.length);
      });
      window.dispatchEvent(new Event('aai:notifications-changed'));
    } catch {
      setError('Could not update notifications.');
    }
  };

  const openOne = (n: AaiNotification) => {
    if (!n.read) markRead([n.id]);
    if (!canOpen(n.link, scope)) return;
    setOpen(false);
    if (clients && n.client_id) clients.open(n.client_id);
    navigate(n.link as string);
  };

  return (
    <div ref={boxRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((was) => !was)}
        title="Notifications"
        aria-haspopup="dialog"
        aria-expanded={open}
        className={clsx(
          'relative inline-flex h-[34px] w-[34px] items-center justify-center rounded-act border border-line bg-white text-ink-control transition-colors hover:bg-canvas-inset',
          open && 'bg-canvas-inset',
        )}
      >
        <Bell size={15} className="shrink-0" />
        {unread > 0 && (
          <span className="absolute -right-1 -top-1 grid h-[17px] min-w-[17px] place-items-center rounded-full bg-accent-500 px-1 font-sans text-[10px] font-bold tabular-nums text-black">
            {countLabel(unread)}
          </span>
        )}
        <span className="sr-only">{unread} unread notifications</span>
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Notifications"
          className="absolute right-0 top-[calc(100%+8px)] z-[90] max-h-[70vh] w-[360px] max-w-[calc(100vw-32px)] overflow-hidden rounded-panel border border-line bg-white shadow-[0_12px_32px_rgba(15,23,42,0.18)]"
        >
          <div className="flex items-center justify-between gap-3 border-b border-line-soft px-4 py-3">
            <span className="text-[13px] font-semibold text-ink">Notifications</span>
            <button
              type="button"
              onClick={() => markRead()}
              disabled={unread === 0}
              className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-ink-dim transition-colors hover:text-ink disabled:opacity-40"
            >
              <CheckCheck size={14} /> Mark all read
            </button>
          </div>

          <div className="max-h-[52vh] overflow-y-auto">
            {loading && <p className="m-0 px-4 py-8 text-center text-[12.5px] text-ink-muted">Loading</p>}
            {!loading && error && <p className="m-0 px-4 py-8 text-center text-[12.5px] text-danger">{error}</p>}
            {!loading && !error && items.length === 0 && (
              <p className="m-0 px-4 py-8 text-center text-[12.5px] text-ink-muted">Nothing waiting on you.</p>
            )}
            {!loading && !error && items.map((n) => (
              <button
                key={n.id}
                type="button"
                onClick={() => openOne(n)}
                disabled={n.read && !canOpen(n.link, scope)}
                className={clsx(
                  'flex w-full gap-3 border-b border-line-soft px-4 py-3 text-left transition-colors last:border-0',
                  canOpen(n.link, scope) && 'hover:bg-canvas-inset',
                  !canOpen(n.link, scope) && 'cursor-default',
                  !n.read && 'bg-canvas-inset',
                )}
              >
                <span
                  aria-hidden
                  className={clsx('mt-[7px] h-2 w-2 shrink-0 rounded-full', !n.read && 'bg-accent-500', n.read && 'bg-transparent')}
                />
                <span className="min-w-0 flex-1">
                  <span className={clsx('block text-[13px] leading-[19px] text-ink', !n.read && 'font-semibold', n.read && 'font-medium')}>
                    {n.title}
                  </span>
                  {n.body && <span className="mt-0.5 block text-[12px] leading-[18px] text-ink-dim">{n.body}</span>}
                  <span className="mt-1 block text-[11px] text-ink-muted">
                    {[sourceOf(n), timeAgo(n.created_at)].filter(Boolean).join(' · ')}
                  </span>
                </span>
                {canOpen(n.link, scope) && <ArrowRight size={15} className="mt-1 shrink-0 text-ink-soft" />}
              </button>
            ))}
          </div>

          {allHref && (
            <button
              type="button"
              onClick={() => { setOpen(false); navigate(allHref); }}
              className="block w-full border-t border-line-soft px-4 py-2.5 text-center text-[12.5px] font-semibold text-ink-dim transition-colors hover:bg-canvas-inset hover:text-ink"
            >
              See all notifications
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
